import { useEffect, useState } from 'preact/hooks';
import { api, flash, type Role } from '../state';

type Agent = { id: number; name: string; email: string; role: Role; offices: string[]; active: boolean };
const blank = { id: 0, name: '', email: '', role: 'agent' as Role, offices: [] as string[], active: true, password: '', invite: true };

export function Agents() {
  const [list, setList] = useState<Agent[]>([]);
  const [offices, setOffices] = useState<string[]>([]);
  const [edit, setEdit] = useState<typeof blank | null>(null);
  const [error, setError] = useState('');
  const load = () => api<Agent[]>('/agents').then(setList);
  useEffect(() => {
    load();
    api<string[]>('/offices').then(setOffices);
  }, []);

  async function invite(a: Agent) {
    if (!confirm(`Email ${a.name} a link to set their password and sign in?`)) return;
    try {
      await api(`/agents/${a.id}/invite`, { method: 'POST' });
      flash(`Invite emailed to ${a.email}.`);
    } catch (err) {
      flash((err as Error).message);
    }
  }

  async function save(e: Event) {
    e.preventDefault();
    if (!edit) return;
    setError('');
    const { id, password, invite, ...body } = edit;
    try {
      if (id) await api(`/agents/${id}`, { method: 'PATCH', body: JSON.stringify(password ? { ...body, password } : body) });
      else await api('/agents', { method: 'POST', body: JSON.stringify(invite ? { ...body, invite: true } : { ...body, password }) });
      flash(!id && invite ? `${edit.name} added — invite emailed to ${edit.email}.` : `${edit.name} saved.`);
      setEdit(null);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div class="page">
      <div class="page-head">
        <h1>Agents</h1>
        <button class="btn primary" onClick={() => setEdit({ ...blank })}>Add agent</button>
      </div>
      <table class="table">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Offices</th><th></th></tr></thead>
        <tbody>
          {list.map((a) => (
            <tr class={a.active ? '' : 'inactive'}>
              <td><strong>{a.name}</strong>{!a.active && <span class="pill missed">Disabled</span>}</td>
              <td>{a.email}</td>
              <td>{a.role}</td>
              <td>{a.offices.join(', ') || '—'}</td>
              <td class="nowrap">
                <button class="link" onClick={() => setEdit({ ...a, password: '', invite: false })}>Edit</button>
                {a.active && <> · <button class="link" onClick={() => invite(a)}>Send invite</button></>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {edit && (
        <div class="drawer" onClick={(e) => e.target === e.currentTarget && setEdit(null)}>
          <form class="drawer-panel form" onSubmit={save}>
            <button type="button" class="link close" onClick={() => setEdit(null)}>Close</button>
            <h2>{edit.id ? `Edit ${edit.name}` : 'Add agent'}</h2>
            <label class="field"><span>Name</span><input value={edit.name} required onInput={(e) => setEdit({ ...edit, name: (e.target as HTMLInputElement).value })} /></label>
            <label class="field"><span>Email</span><input type="email" value={edit.email} required onInput={(e) => setEdit({ ...edit, email: (e.target as HTMLInputElement).value })} /></label>
            <label class="field"><span>Role</span>
              <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: (e.target as HTMLSelectElement).value as Role })}>
                <option value="agent">Agent — answers chats</option>
                <option value="manager">Manager — chats, leads, reports</option>
                <option value="admin">Admin — everything, plus agents</option>
              </select>
            </label>
            <fieldset class="field"><span>Offices this person answers chats for</span>
              {offices.map((o) => (
                <label class="check">
                  <input type="checkbox" checked={edit.offices.includes(o)} onChange={(e) => setEdit({ ...edit, offices: (e.target as HTMLInputElement).checked ? [...edit.offices, o] : edit.offices.filter((x) => x !== o) })} />
                  {o}
                </label>
              ))}
              {!offices.length && <p class="muted small">Offices load from WordPress → Forms → Lead Routing.</p>}
            </fieldset>
            {!edit.id && (
              <label class="check">
                <input type="checkbox" checked={edit.invite} onChange={(e) => setEdit({ ...edit, invite: (e.target as HTMLInputElement).checked })} />
                Email them an invite to set their own password
              </label>
            )}
            {(edit.id > 0 || !edit.invite) && (
              <label class="field"><span>{edit.id ? 'New password (leave blank to keep)' : 'Password (10+ characters)'}</span>
                <input type="text" value={edit.password} minLength={10} required={!edit.id} autocomplete="new-password" onInput={(e) => setEdit({ ...edit, password: (e.target as HTMLInputElement).value })} />
              </label>
            )}
            {edit.id > 0 && <label class="check"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: (e.target as HTMLInputElement).checked })} /> Active (can sign in)</label>}
            {error && <p class="error">{error}</p>}
            <button class="btn primary">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
